import { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PaymentEmptyState from '../components/PaymentEmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { useNavigate, Navigate } from '../routing';
import { paymentsApi, documentsApi, suppliersApi, formatCurrencyWhole } from '../utils/api';
import { track, EVENTS } from '../utils/analytics';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useSortable } from '../utils/useSortable';
import { readSharedDateRange, writeSharedDateRange } from '../utils/sharedDateRange';
import { setSupplierJump, takeSupplierJump } from '../utils/supplierJump';
import { paidDocsInMonth } from '../utils/paidDocsInMonth';
import { formatDateShort } from '../utils/dateFormat';
import { currentMonthRange, periodLabel, PRESETS_MONTHLY } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import { PaymentMethodIcon, paymentMethodIcon } from './SupplierManagementPage';
import PaymentsFilterBar from '../components/PaymentsFilterBar';
import { useIsMobile } from '../utils/useIsMobile';
import ListRow from '../components/ListRow';
import { PaymentLifecycleBadge, LifecycleHelp } from '../components/PaymentLifecycleBadge';
import PaymentTrackingHelp from '../components/PaymentTrackingHelp';
import { isPaidTabState, approvalOfferForRange } from '../utils/paymentLifecycle';
import { RECORDABLE_DOC_TYPES, isCreditNoteDoc } from '../utils/docTypes';
import PaymentViewTabs, { consumeViewEntry } from '../components/PaymentViewTabs';
import PaymentModal from '../components/PaymentModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
import CommentsModal from '../components/CommentsModal';
import { toast } from '../components/Toast';
import Icon from '../components/Icon';
import LinkedNoteMark from '../components/LinkedNoteMark';
import { PURCHASE_INFO } from '../components/StatInfo';
import StatCardsRow from '../components/StatCardsRow';
import { useConfirm } from '../utils/useConfirm';
import HqOnlyNotice, { HqOnlyLock } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';

// Docs that can carry a payment row (mirrors the server's
// RECORDABLE_DOC_TYPES = PAYABLE_DOC_TYPES + חשבונית זיכוי). The drill-down
// endpoint returns every doc type, incl. receipts; we filter to these so the
// detail reconciles with the collapsed aggregate, which counts a recorded
// credit note as a (negative) payment (owner, 2026-09-15).
const RECORDABLE_TYPES = new Set(RECORDABLE_DOC_TYPES);

// A credit note reduces what was paid, so its amount reads as a minus — the
// same "-₪X in red" the supplier's documents page uses — whether it was stored
// with a positive or a negative total.
const signedDocTotal = (d) => (isCreditNoteDoc(d)
  ? formatCurrencyWhole(-Math.abs(Number(d.total) || 0) || 0)
  : formatCurrencyWhole(d.total));

// Tolerant method compare: strip ASCII apostrophe / Hebrew geresh so a manually
// recorded "צ'ק" matches the "צ׳ק" option, etc.
const normMethod = (s) => String(s || '').trim().replace(/['׳]/g, '');


// 'YYYY-MM-DD' -> 'dd/mm/yy'.
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y.slice(-2)}`;
};

// This is a tracker of payments that WERE made, so the rows are LOCKED to the
// lifecycle states with money against them — שולם + שולם חלקית
// (isPaidTabState in utils/paymentLifecycle.js, paired with the server lib).
// There is no status filter on this screen: unpaid suppliers never appear
// (they belong on the Suppliers page's open-items view, not here).

// Per user and per restaurant — see filtersKey in SuppliersPage.
const filtersKey = (uid, rid) => `peak.paymentsFilters.v2.${uid ?? 'anon'}.${rid ?? 'all'}`;
function readSavedFilters(uid, rid) {
  try { return JSON.parse(sessionStorage.getItem(filtersKey(uid, rid))) || {}; }
  catch { return {}; }
}

// Renders the set of payment-method icons actually used by a supplier. Falls
// back to a dot when nothing has been paid yet.
function MethodIcons({ methods }) {
  if (!methods || methods.length === 0) return <span style={{ color: '#cbd5e1' }}>·</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {methods.map(m => (
        // The icon stands alone here, so an unrecognized method still needs a
        // mark — otherwise the payment vanishes from the column entirely. The
        // `title` keeps the raw value reachable on hover either way.
        <span key={m} title={m} style={{ display: 'inline-flex' }}>
          {paymentMethodIcon(m)
            ? <PaymentMethodIcon method={m} size={15} />
            : <span style={{ color: '#cbd5e1' }}>·</span>}
        </span>
      ))}
    </span>
  );
}

export default function PaymentsPage() {
  const { user, restaurantId, canEdit, isAdmin } = useAuth();
  const [askConfirm, confirmDialog] = useConfirm();
  const { paymentTrackingEnabled } = useSettings();
  const navigate = useNavigate();

  // One per visit to this view — see the matching effect on SuppliersPage.
  useEffect(() => {
    if (paymentTrackingEnabled) {
      track(EVENTS.PAYMENT_VIEW_SHOWN, { view: 'paid', entry: consumeViewEntry() });
    }
  }, [paymentTrackingEnabled]);

  const [rows, setRows]       = useState([]);
  // Table vs cards switches with the filter bar and the view tabs, at the
  // filter-system breakpoint — not at 640, which is the DENSITY step.
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Document comments thread (comments + payment notes) opened from a sub-row.
  const [docComments, setDocComments] = useState(null);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [pageMenuOpen,    setPageMenuOpen]    = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Filters (sticky per user in this browser session).
  const saved = useMemo(() => readSavedFilters(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  // One-shot supplier name handed over from the לתשלום tab's "show in history"
  // link — wins over the sticky saved value on this mount only. Ref guard (not
  // useMemo/useState) so the read-and-delete runs exactly once even under a
  // StrictMode double-invoke — see SuppliersPage.
  const jumpRef = useRef(null);
  if (jumpRef.current === null) jumpRef.current = takeSupplierJump(user?.id);
  const jumpName = jumpRef.current;
  const defaultRange = useMemo(() => currentMonthRange(), []);
  // The date range is SHARED with the Suppliers page — prefer whatever period was
  // last viewed on either screen for this restaurant, falling back to the
  // current month.
  const sharedDate = useMemo(() => readSharedDateRange(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [nameFilter,   setNameFilter]   = useState(jumpName || saved.nameFilter || '');
  const [methodFilter, setMethodFilter] = useState(saved.methodFilter ?? []);
  const [dateFrom,     setDateFrom]     = useState(sharedDate?.from ?? defaultRange.from);
  const [dateTo,       setDateTo]       = useState(sharedDate?.to ?? defaultRange.to);
  // Rolls a preset forward when the tab comes back after the day/month changed,
  // and is the key the period is saved under (utils/usePresetPeriod.js).
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_MONTHLY, (f, t) => { setDateFrom(f); setDateTo(t); });

  // Drill-down: one supplier open at a time, docs lazy-loaded + cached.
  const [expandedId, setExpandedId]   = useState(null);
  const [docsCache,  setDocsCache]    = useState({});   // supplierId -> docs[]
  const [docsLoading, setDocsLoading] = useState(false);

  const [paymentModal, setPaymentModal] = useState(null);   // { supplierId, supplierName, documentId?, documentInfo?, dateFrom?, dateTo? }
  const [historyModal, setHistoryModal] = useState(null);   // { supplierId, supplierName }
  const [openMenu,     setOpenMenu]     = useState(null);   // 'sup-<id>' | 'doc-<id>'

  // Token guard against out-of-order responses — see SuppliersPage.
  // See SuppliersPage: which period the rows in state were fetched for.
  const [loadedKey, setLoadedKey] = useState(null);
  const periodKey = `${dateFrom}|${dateTo}`;
  const loadToken = useRef(0);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(false);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await paymentsApi.getOverview(params);
      if (token !== loadToken.current) return;   // superseded
      setRows(res.data);
      setLoadedKey(`${dateFrom}|${dateTo}`);
    } catch {
      // A failed fetch must never fall through to the empty state — "no rows"
      // and "the request failed" are different facts (see InlineLoadError).
      if (token !== loadToken.current) return;
      setLoadError(true);
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Persist name/method filters on every change (date lives in the shared range).
  useEffect(() => {
    if (!user?.id) return;
    try {
      sessionStorage.setItem(filtersKey(user.id, restaurantId), JSON.stringify({ nameFilter, methodFilter }));
    } catch (err) {
      // Storage disabled or full — non-fatal, the filters just won't stick.
      console.warn('payments filters not saved', err);
    }
  }, [user?.id, restaurantId, nameFilter, methodFilter]);

  // Mirror the date range to the shared key so the Suppliers page picks it up.
  useEffect(() => {
    if (user?.id) writeSharedDateRange(user.id, restaurantId, presetKey, dateFrom, dateTo);
  }, [user?.id, restaurantId, presetKey, dateFrom, dateTo]);

  // Re-fetch on tab focus so auto-pay / edits made elsewhere reflect here.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const fetchDocs = useCallback(async (supplierId) => {
    setDocsLoading(true);
    try {
      const res = await documentsApi.getBySupplier(supplierId);
      const payable = res.data.filter(d => RECORDABLE_TYPES.has(d.doc_type));
      setDocsCache(prev => ({ ...prev, [supplierId]: payable }));
    } finally {
      setDocsLoading(false);
    }
  }, []);

  const toggleRow = (supplierId) => {
    setOpenMenu(null);
    if (expandedId === supplierId) { setExpandedId(null); return; }
    setExpandedId(supplierId);
    if (!docsCache[supplierId]) fetchDocs(supplierId);
  };

  // After any payment mutation: refresh the aggregate AND the open detail.
  const refreshAfterMutation = async (supplierId) => {
    await load();
    if (supplierId) await fetchDocs(supplierId);
  };

  const handleDeleteDocPayment = (doc, supplierId) => {
    setOpenMenu(null);
    // Mirrors the supplier-documents page: this endpoint removes EVERY payment
    // on the document, and since partial payments a document can carry several.
    const n = doc.payment_count || 0;
    askConfirm({
      title: n > 1 ? 'מחיקת התשלומים' : 'מחיקת תשלום',
      message: n > 1
        ? `למחוק את ${n} התשלומים שנרשמו על ${doc.doc_type}${doc.doc_num ? ` · ${doc.doc_num}` : ''}?`
        : `למחוק את התשלום שנרשם על ${doc.doc_type}${doc.doc_num ? ` · ${doc.doc_num}` : ''}?`,
      detail: `${formatCurrencyWhole(doc.payment_paid_total != null ? doc.payment_paid_total : doc.total)} יימחקו, והמסמך יחזור להיות פתוח לתשלום. הפעולה נרשמת ביומן.`,
      confirmLabel: n > 1 ? 'מחק תשלומים' : 'מחק תשלום',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await paymentsApi.deleteByDocument(doc.id);
          toast(n > 1 ? 'רישומי התשלום נמחקו' : 'רישום התשלום נמחק');
          await refreshAfterMutation(supplierId);
        } catch {
          toast('שגיאה במחיקת התשלום', 'error');
        }
      },
    });
  };

  // Client-side categorical filtering over the (small) aggregate set. The rows
  // are LOCKED to paid + partial — unpaid suppliers are never shown here.
  const filtered = useMemo(() => rows.filter(r => {
    if (!isPaidTabState(r.lifecycle?.state)) return false;
    if (nameFilter && !r.name.includes(nameFilter)) return false;
    if (methodFilter.length > 0 &&
        !methodFilter.some(mf => r.methods_used.some(mu => normMethod(mu) === normMethod(mf)))) return false;
    return true;
  }), [rows, nameFilter, methodFilter]);

  const { sorted, sort: paySort, setSortTo: setPaySort, indicator, headerProps } = useSortable(filtered, {
    name:   r => r.name,
    paid:   r => r.amount_paid || 0,
    unpaid: r => r.amount_unpaid || 0,
    date:   r => (r.last_payment_date ? new Date(r.last_payment_date) : null),
  });

  const totals = useMemo(() => ({
    suppliers: filtered.length,
    paid:   filtered.reduce((s, r) => s + (r.amount_paid || 0), 0),
    unpaid: filtered.reduce((s, r) => s + (r.amount_unpaid || 0), 0),
  }), [filtered]);

  // Four metric cards for the שולם view, all scoped to the filtered rows.
  // `paid_docs` is already on the overview payload (server/routes/payments.js)
  // and was previously unused in the client. Amounts derive from d.total, which
  // is VAT-INCLUSIVE — hence the מע"מ label rather than a separate calculation.
  // Order is ספקים → מסמכים → שולם → הוצאות, matching the לתשלום tab so the row
  // never reshuffles when you switch tabs. "שולם" is what was actually PAID,
  // mirroring the other tab's "סה"כ לתשלום"; "הוצאות" is the gross total (paid +
  // still owed) for these suppliers, matching the other tab's expenses card.
  const paidDocs = filtered.reduce((s, r) => s + (r.paid_docs || 0), 0);
  // The "סה"כ רכש (כולל מע"מ)" card answers "how much did I buy this period" — a fact about the
  // PERIOD, not about this tab, so it has to read identically on both tabs.
  // It therefore sums `rows` (every supplier the server returned for the range)
  // and NOT `filtered`, which is locked to suppliers that have paid something:
  // a supplier you never paid is still something you bought, and excluding it
  // made this card read lower here than on לתשלום for the same month.
  //
  // Only the name search narrows it, matching `filteredNoPayment` on
  // SuppliersPage. The method filter deliberately does not: a supplier with no
  // payments has no payment method, so filtering by one would drop exactly the
  // suppliers this fix exists to include. `purchase_amount` is server-computed
  // from the same doc scope + credit rules as the לתשלום tab (see payments.js).
  const grossTotal = rows.reduce(
    (s, r) => (!nameFilter || r.name.includes(nameFilter)) ? s + (r.purchase_amount || 0) : s,
    0,
  );
  // Owner indicator: the HQ-only part of that same רכש card.
  const hiddenGross = rows.reduce(
    (s, r) => (r.hq_only && (!nameFilter || r.name.includes(nameFilter))) ? s + (r.purchase_amount || 0) : s,
    0,
  );
  const statCards = [
    { key: 'suppliers', value: totals.suppliers,                    label: 'סה"כ ספקים' },
    { key: 'docs',      value: paidDocs,                            label: 'סה"כ מסמכים ששולמו' },
    { key: 'total',     value: formatCurrencyWhole(totals.paid),         label: 'סה"כ שולם (כולל מע"מ)' },
    { key: 'gross',     value: formatCurrencyWhole(grossTotal),          label: 'סה"כ רכש (כולל מע"מ)',
      info: PURCHASE_INFO },
  ];

  const clearFilters = () => {
    const fresh = currentMonthRange();
    setNameFilter('');
    setMethodFilter([]);
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
  };

  // Empty-state message for the היסטורית תשלומים list. `rows` still holds the
  // fully-UNPAID suppliers (server returns them; the isPaidTabState filter hides
  // them from display), so a name that matches a row while `filtered` is empty
  // means "this supplier exists in range but nothing's paid yet" — no extra call.
  // The method-filter overflow falls back to the generic line.
  const hasDateRange    = !!(dateFrom || dateTo);
  const emptyKind = (() => {
    if (methodFilter.length > 0) return 'fallback';
    if (nameFilter) {
      if (rows.some(r => r.name.includes(nameFilter))) return 'nothing-paid';
      return hasDateRange ? 'no-match-range' : 'unknown';
    }
    if (rows.length === 0 && !hasDateRange) return 'new-account';
    return 'empty-range';
  })();
  const jumpToUnpaid = () => {
    setSupplierJump(user?.id, nameFilter);
    navigate('/suppliers');
  };
  const searchAllDates = () => { setDateFrom(''); setDateTo(''); };

  // Gate: the whole feature is off for restaurants without payment tracking.
  if (!paymentTrackingEnabled) return <Navigate to="/suppliers" replace />;

  const menuBtn = { display: 'block', width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '0.88rem', color: '#333', fontFamily: 'inherit', whiteSpace: 'nowrap' };

  const COLSPAN = 7;

  return (
    // .suppliers-page is the mobile-density scope from SuppliersPage.jsx,
    // reused here rather than duplicated — /suppliers and /suppliers/paid are
    // "two tabs of one screen" (see the title comment below), so they get one
    // shared scope for the header/tabs/stats/list-density rules in index.css,
    // not two near-identical copies of the same CSS block.
    <div className="page suppliers-page">
      {/* `.page-title-row` + `.page-title`, same as every other page. This used
          to be a bare <h1> inside an undefined `.page-header`, so it rendered at
          the UA default 2rem — visibly larger than the 1.6rem title on the
          לתשלום tab, making the header jump on every tab switch. */}
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">ספקים - מעקב תשלומים</h1>
          <HqOnlyNotice money={formatCurrencyWhole} amount={loadError ? null : hiddenGross} pending={loading || loadedKey !== periodKey} basis="רכש בתקופה (כולל מע״מ)" />
        </div>
        <div className="page-title-actions">
          <div className="page-menu">
            <button className="btn btn-sm btn-excel"
              aria-expanded={reportsMenuOpen}
              onClick={() => setReportsMenuOpen(o => !o)}>
              <Icon name="spreadsheet" size={14} />
              דוחות
            </button>
            {reportsMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setReportsMenuOpen(false)} />
                <div className="page-menu-panel">
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'suppliers' });
                      suppliersApi.exportExcel({
                        ...(dateFrom ? { date_from: dateFrom } : {}),
                        ...(dateTo   ? { date_to:   dateTo   } : {}),
                      });
                    }}
                    title="שורה לכל ספק עם סכום, שולם, יתרה לתשלום, סטטוס, תנאי ותאריך תשלום והערות — ובגיליון נוסף פירוט כל תשלום (אמצעי, הערות, נרשם ע״י)">
                    <Icon name="spreadsheet" size={15} /> סטטוס תשלומים לפי ספק
                  </button>
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      documentsApi.exportAllPayments({
                        ...(dateFrom ? { date_from: dateFrom } : {}),
                        ...(dateTo   ? { date_to:   dateTo   } : {}),
                      });
                    }}
                    title="מספר מסמך, סכום לפני/כולל מע״מ, תנאי תשלום, תאריך לתשלום, סטטוס והערות — לכל מסמך">
                    <Icon name="spreadsheet" size={15} /> דוח תשלומים מפורט (לפי מסמך)
                  </button>
                </div>
              </>
            )}
          </div>
          {/* The ⋮ lives on BOTH tabs of this screen. They share one title and
              one header, so a control that appears on לתשלום and vanishes on
              היסטורית תשלומים reads as the page losing an ability mid-task.
              It carries only רענן here: הוסף ספק is a /suppliers action and
              there is no supplier-creation modal on this tab to open. */}
          <div className="page-menu">
            <button
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel">
                  <button className="page-menu-item" disabled={loading}
                    onClick={() => { setPageMenuOpen(false); load(); }}
                    title="טען מחדש מהמסד נתונים">
                    <Icon name="recurring" size={15} /> רענן
                  </button>
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); setHelpOpen(true); }}>
                    <Icon name="info" size={15} /> חשוב לדעת
                  </button>
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
          {helpOpen && <PaymentTrackingHelp tab="paid" onClose={() => setHelpOpen(false)} />}
        </div>
      </div>

      {/* The view switch replaces the old "חזרה לספקים" button: this screen is
          a peer view of /suppliers, not a child of it, so the way back is the
          same control that got you here. Tabs dock inside .card below, at
          every breakpoint — same placement as /suppliers (see the comment
          there for why the old mobile-only "above the filter bar" placement
          was dropped). */}
      <PaymentsFilterBar
        nameFilter={nameFilter}     setNameFilter={setNameFilter}
        methodFilter={methodFilter} setMethodFilter={setMethodFilter}
        dateFrom={dateFrom} dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        onClearAll={clearFilters}
        resultCount={filtered.length}
        sortOptions={[
          { key: 'unpaid', label: 'לא שולם' },
          { key: 'paid',   label: 'שולם' },
          { key: 'date',   label: 'תשלום אחרון' },
          { key: 'name',   label: 'שם ספק' },
        ]}
        sort={paySort}
        onSortChange={setPaySort}
      />

      {/* Hidden on a zero-result view, and on a load failure — see SuppliersPage
          for the rationale on both. */}
      {!loadError && filtered.length > 0 && (
        <StatCardsRow cards={statCards} loading={loading || loadedKey !== periodKey} />
      )}

      <div className="card">
        {/* The card's own header row, on every breakpoint — see SuppliersPage. */}
        <PaymentViewTabs active="paid" />

        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את התשלומים" onRetry={load} />
        ) : filtered.length === 0 ? (
          <PaymentEmptyState
            period={dateFrom || dateTo ? periodLabel(dateFrom, dateTo) : ''}
            tab="paid"
            kind={emptyKind}
            supplierName={nameFilter}
            onJump={jumpToUnpaid}
            onSearchAllDates={searchAllDates}
          />
        ) : (
          <>
          {!isMobile && (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th {...headerProps('name')}>ספק{indicator('name')}</th>
                  <th {...headerProps('paid')}>שולם{indicator('paid')}</th>
                  <th {...headerProps('unpaid')}>לא שולם{indicator('unpaid')}</th>
                  <th>אמצעי תשלום</th>
                  <th {...headerProps('date')}>תשלום אחרון{indicator('date')}</th>
                  <th>מצב תשלום<LifecycleHelp /></th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
                  const open = expandedId === s.id;
                  // Drill-down shows only the PAID invoices dated in the filtered
                  // month — reconciles with the row's "שולם" amount (server scopes
                  // amount_paid the same way). Cache holds all payable docs; we
                  // re-filter on render so changing the month needs no refetch.
                  const docs = paidDocsInMonth(docsCache[s.id] || [], dateFrom, dateTo);
                  return (
                    <Fragment key={s.id}>
                      <tr className="row-clickable" onClick={() => toggleRow(s.id)}
                        style={{ background: open ? '#eaf0f8' : undefined }}>
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <Icon name="chevron" size={13} style={{ transform: open ? 'none' : 'rotate(90deg)', color: '#94a3b8' }} />
                            {s.name}<HqOnlyLock hqOnly={s.hq_only} />
                          </span>
                        </td>
                        <td style={{ color: '#15803d', fontWeight: 600 }}>{formatCurrencyWhole(s.amount_paid)}</td>
                        <td style={{ color: s.amount_unpaid > 0 ? '#b91c1c' : '#94a3b8', fontWeight: 600 }}>{formatCurrencyWhole(s.amount_unpaid)}</td>
                        <td><MethodIcons methods={s.methods_used} /></td>
                        <td>{fmtDate(s.last_payment_date)}</td>
                        <td><PaymentLifecycleBadge lifecycle={s.lifecycle} /></td>
                        <td style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                          <button
                            className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ink-muted)', padding: '2px 4px', lineHeight: 1 }}
                            onClick={() => setOpenMenu(openMenu === `sup-${s.id}` ? null : `sup-${s.id}`)}
                            title="פעולות">⋮</button>
                          {openMenu === `sup-${s.id}` && (
                            <div style={{ position: 'absolute', insetInlineEnd: 4, top: '100%', zIndex: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', minWidth: 180, overflow: 'hidden' }}>
                              {canEdit && (
                                <button className="row-menu-item" style={{ ...menuBtn, color: '#15803d', fontWeight: 600 }}
                                  onClick={() => { setOpenMenu(null); setPaymentModal({ supplierId: s.id, supplierName: s.name, dateFrom, dateTo, lifecycle: s.lifecycle }); }}>
                                  <Icon name="card" size={16} /> בוצע תשלום
                                </button>
                              )}
                              <button className="row-menu-item" style={menuBtn}
                                onClick={() => { setOpenMenu(null); setHistoryModal({ supplierId: s.id, supplierName: s.name }); }}>
                                <Icon name="history" size={16} /> היסטוריית תשלומים
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>

                      {open && (
                        <tr>
                          <td colSpan={COLSPAN} style={{ padding: 0, background: '#fafbfc', borderTop: '1px solid #e5ebf5' }}>
                            {docsLoading && !docsCache[s.id] ? (
                              <div style={{ padding: 16, color: 'var(--ink-muted)' }}>טוען מסמכים...</div>
                            ) : docs.length === 0 ? (
                              <div style={{ padding: 16, color: '#94a3b8' }}>אין חשבוניות ששולמו בחודש זה</div>
                            ) : (
                              <table style={{ margin: 0 }}>
                                <thead>
                                  <tr>
                                    <th>מסמך</th>
                                    <th>תאריך</th>
                                    <th>סכום</th>
                                    <th>סטטוס</th>
                                    <th>אמצעי</th>
                                    <th>תאריך תשלום</th>
                                    <th style={{ width: 60 }}>הערות</th>
                                    <th style={{ width: 36 }}></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {docs.map(d => {
                                    const paid = !!d.payment_fully_paid;
                                    const partial = !paid && Number(d.payment_paid_total) > 0.005;
                                    return (
                                      <tr key={d.id}>
                                        <td style={{ fontSize: '0.85rem' }}>
                                          <button
                                            style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', padding: 0, font: 'inherit' }}
                                            onClick={() => navigate(`/document/${d.id}`)}>
                                            {d.doc_type}{d.doc_num ? ` · ${d.doc_num}` : ''}
                                          </button>
                                          {/* Admin only. A linked note is normally paid through
                                              its מרכזת, so seeing one here means it was paid
                                              directly too — worth a second look. */}
                                          {isAdmin && d.linked_invoice_doc_num != null && (
                                            <LinkedNoteMark invoiceDocNum={d.linked_invoice_doc_num} showTip />
                                          )}
                                        </td>
                                        <td style={{ fontSize: '0.85rem' }}>{formatDateShort(d.date) || '—'}</td>
                                        <td style={{ fontSize: '0.85rem', ...(isCreditNoteDoc(d) ? { color: '#c0392b' } : {}) }}>{signedDocTotal(d)}</td>
                                        <td>
                                          <span style={{ color: paid ? '#15803d' : partial ? '#b45309' : '#b91c1c', fontWeight: 600, fontSize: '0.82rem' }}>
                                            {paid
                                              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check-circle" size={13} /> שולם</span>
                                              : partial
                                                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="alert" size={13} /> שולם חלקית</span>
                                                : 'לא שולם'}
                                          </span>
                                        </td>
                                        <td style={{ fontSize: '0.85rem' }}>
                                          {paid ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><PaymentMethodIcon method={d.payment_method} size={14} /> {d.payment_method}</span> : '—'}
                                        </td>
                                        <td style={{ fontSize: '0.85rem' }}>{fmtDate(d.payment_date)}</td>
                                        {/* The document's notes — comments AND payment notes
                                            (the "why is this paid" written when a draft was
                                            marked שולם) — same note-cell as the supplier
                                            page, opening the same thread. */}
                                        <td>
                                          {(() => {
                                            const n = (d.comment_count || 0) + (d.payment_note_count || 0);
                                            return (
                                              <button type="button" className={`note-cell${n > 0 ? ' is-active' : ''}`}
                                                onClick={() => setDocComments({ referenceId: d.id, title: `מסמך ${d.doc_num || d.id}` })}
                                                data-tip={n > 0 ? `${n} הערות` : 'הוסף הערה'}
                                                aria-label={n > 0 ? `הערות: ${n}` : 'הוסף הערה'}>
                                                <Icon name="comment" size={14} />
                                                {n > 0 && <span>{n}</span>}
                                              </button>
                                            );
                                          })()}
                                        </td>
                                        <td style={{ position: 'relative' }}>
                                          <button
                                            className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.05rem', color: 'var(--ink-muted)', padding: '2px 4px', lineHeight: 1 }}
                                            onClick={() => setOpenMenu(openMenu === `doc-${d.id}` ? null : `doc-${d.id}`)}
                                            title="פעולות">⋮</button>
                                          {openMenu === `doc-${d.id}` && (
                                            <div style={{ position: 'absolute', insetInlineEnd: 4, top: '100%', zIndex: 20, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden' }}>
                                              {!paid && canEdit && (
                                                <button className="row-menu-item" style={{ ...menuBtn, color: '#15803d', fontWeight: 600 }}
                                                  onClick={() => { setOpenMenu(null); setPaymentModal({ supplierId: s.id, supplierName: s.name, documentId: d.id, documentInfo: { doc_type: d.doc_type, doc_num: d.doc_num, total: d.total } }); }}>
                                                  <Icon name="card" size={16} /> רשום תשלום
                                                </button>
                                              )}
                                              {paid && isAdmin && (
                                                <button className="row-menu-item" style={{ ...menuBtn, color: '#b91c1c' }}
                                                  onClick={() => handleDeleteDocPayment(d, s.id)}>
                                                  <Icon name="trash" size={16} /> מחק תשלום
                                                </button>
                                              )}
                                              {paid && !isAdmin && (
                                                <div style={{ ...menuBtn, color: '#94a3b8', cursor: 'default' }}>אין פעולות</div>
                                              )}
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e5e7eb', background: '#f8fafc' }}>
                  <td style={{ padding: '10px 16px' }}>סה"כ ({totals.suppliers} ספקים)</td>
                  <td style={{ padding: '10px 16px', color: '#15803d' }}>{formatCurrencyWhole(totals.paid)}</td>
                  <td style={{ padding: '10px 16px', color: totals.unpaid > 0 ? '#b91c1c' : '#94a3b8' }}>{formatCurrencyWhole(totals.unpaid)}</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}

          {/* Mobile (≤640px): accordion list rows — supplier / unpaid amount
              up top, tap expands to simplified paid-doc sub-rows + the two
              actions. Same sorted list + docsCache as the desktop table. */}
          {isMobile && (
          <div>
            <div className="m-list">
              {sorted.map(s => {
                const open = expandedId === s.id;
                const docs = paidDocsInMonth(docsCache[s.id] || [], dateFrom, dateTo);
                return (
                  <Fragment key={s.id}>
                    <ListRow
                      variant="accordion"
                      expanded={open}
                      primary={<>{s.name}<HqOnlyLock hqOnly={s.hq_only} /></>}
                      // The last-payment date and the remaining balance used to
                      // be two stacked lines (meta, then a separate endSub under
                      // the amount) — folded into one meta line, same merge as
                      // SuppliersPage's mobile rows.
                      meta={
                        <>
                          {s.last_payment_date && <span>תשלום אחרון {fmtDate(s.last_payment_date)}</span>}
                          {s.amount_unpaid > 0 && (
                            <span style={{ color: '#b91c1c', fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {s.last_payment_date ? ' · ' : ''}נותר {formatCurrencyWhole(s.amount_unpaid)}
                            </span>
                          )}
                        </>
                      }
                      badge={<PaymentLifecycleBadge lifecycle={s.lifecycle} />}
                      // This is the payment-HISTORY tab, so the hero is what was
                      // PAID (green), matching the desktop's leading שולם column —
                      // not the owed amount (a fully-paid supplier led with a grey
                      // ₪0.00).
                      amount={<span style={{ color: '#15803d' }}>{formatCurrencyWhole(s.amount_paid)}</span>}
                      onClick={() => toggleRow(s.id)}
                    />
                    {open && (
                      <div className="m-subrows">
                        {docsLoading && !docsCache[s.id] ? (
                          <div style={{ padding: 12, color: 'var(--ink-muted)', fontSize: 13 }}>טוען מסמכים...</div>
                        ) : docs.length === 0 ? (
                          <div style={{ padding: 12, color: '#94a3b8', fontSize: 13 }}>אין חשבוניות ששולמו בחודש זה</div>
                        ) : (
                          docs.map(d => {
                            const paid = !!d.payment_fully_paid;
                                    const partial = !paid && Number(d.payment_paid_total) > 0.005;
                            return (
                              <div
                                key={d.id}
                                className="m-subrow"
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/document/${d.id}`)}
                              >
                                <div className="m-subrow-main">
                                  <div className="m-subrow-primary">
                                    {d.doc_type}{d.doc_num ? ` · ${d.doc_num}` : ''}
                                    {/* Icon only on a phone — no hover to reveal a tip. */}
                                    {isAdmin && d.linked_invoice_doc_num != null && (
                                      <LinkedNoteMark invoiceDocNum={d.linked_invoice_doc_num} />
                                    )}
                                  </div>
                                  <div className="m-subrow-meta">
                                    {formatDateShort(d.date) || '—'}
                                    {paid && (
                                      <>
                                        {' · '}
                                        {/* Icon-only slot, and the row's separator is
                                            already a '·' — so an unrecognized method
                                            falls back to its own name rather than a
                                            second dot nobody could tell apart. */}
                                        {paymentMethodIcon(d.payment_method)
                                          ? <span title={d.payment_method} style={{ display: 'inline-flex' }}>
                                              <PaymentMethodIcon method={d.payment_method} size={13} />
                                            </span>
                                          : <span>{d.payment_method}</span>}
                                        {` ${fmtDate(d.payment_date)}`}
                                      </>
                                    )}
                                  </div>
                                </div>
                                <span className="m-subrow-amount" style={isCreditNoteDoc(d) ? { color: '#c0392b' } : undefined}>{signedDocTotal(d)}</span>
                                <span style={{ color: paid ? '#15803d' : partial ? '#b45309' : '#b91c1c', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {paid
                                              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="check-circle" size={13} /> שולם</span>
                                              : partial
                                                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="alert" size={13} /> שולם חלקית</span>
                                                : 'לא שולם'}
                                </span>
                                {(() => {
                                  const n = (d.comment_count || 0) + (d.payment_note_count || 0);
                                  return (
                                    <button type="button" className={`note-cell${n > 0 ? ' is-active' : ''}`}
                                      onClick={e => { e.stopPropagation(); setDocComments({ referenceId: d.id, title: `מסמך ${d.doc_num || d.id}` }); }}
                                      aria-label={n > 0 ? `הערות: ${n}` : 'הוסף הערה'}>
                                      <Icon name="comment" size={14} />
                                      {n > 0 && <span>{n}</span>}
                                    </button>
                                  );
                                })()}
                              </div>
                            );
                          })
                        )}
                        <div className="m-subrows-footer" style={{ display: 'flex', gap: 8 }}>
                          {canEdit && (
                            <button className="btn btn-sm btn-success" style={{ flex: 1, justifyContent: 'center' }}
                              onClick={() => setPaymentModal({ supplierId: s.id, supplierName: s.name, dateFrom, dateTo, lifecycle: s.lifecycle })}>
                              <Icon name="card" size={16} /> בוצע תשלום
                            </button>
                          )}
                          <button className="btn btn-sm btn-secondary" style={{ flex: 1, justifyContent: 'center' }}
                            onClick={() => setHistoryModal({ supplierId: s.id, supplierName: s.name })}>
                            <Icon name="history" size={16} /> היסטוריה
                          </button>
                        </div>
                      </div>
                    )}
                  </Fragment>
                );
              })}
              <div className="m-list-foot">
                <span>סה"כ ({totals.suppliers} ספקים)</span>
                {/* Match the rows' hero: this tab totals what was PAID (green). */}
                <span className="m-row-amount" style={{ color: '#15803d' }}>
                  {formatCurrencyWhole(totals.paid)}
                </span>
              </div>
            </div>
          </div>
          )}
          </>
        )}
      </div>

      {paymentModal && (
        <PaymentModal
          supplierId={paymentModal.supplierId}
          supplierName={paymentModal.supplierName}
          documentId={paymentModal.documentId}
          documentInfo={paymentModal.documentInfo}
          dateFrom={paymentModal.dateFrom}
          dateTo={paymentModal.dateTo}
          // Supplier-level only: the per-document entry here carries no
          // lifecycle, so it makes no offer (the helper returns null).
          approvalOffer={approvalOfferForRange({ lifecycle: paymentModal.lifecycle, dateFrom: paymentModal.dateFrom, dateTo: paymentModal.dateTo })}
          onSuccess={() => { const sid = paymentModal.supplierId; setPaymentModal(null); refreshAfterMutation(sid); }}
          onClose={() => setPaymentModal(null)}
        />
      )}

      {docComments && (
        <CommentsModal
          money={formatCurrencyWhole}
          type="document"
          referenceId={docComments.referenceId}
          title={docComments.title}
          onClose={() => setDocComments(null)}
          onCommentAdded={() => setDocsCache(prev => {
            const next = { ...prev };
            for (const sid of Object.keys(next)) {
              next[sid] = (next[sid] || []).map(d => d.id === docComments.referenceId ? { ...d, comment_count: (d.comment_count || 0) + 1 } : d);
            }
            return next;
          })}
        />
      )}
      {historyModal && (
        <PaymentHistoryModal
          supplierId={historyModal.supplierId}
          supplierName={historyModal.supplierName}
          canDelete={isAdmin}
          onChange={() => refreshAfterMutation(historyModal.supplierId)}
          onClose={() => setHistoryModal(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}
